import axiosInstance from "../utils/axios";
import { getCompanyIdFromToken, getUserIdFromToken } from "../utils/tokenUtils";

// API Endpoints
const API_ENDPOINTS = {
  LEADS: "api/Leads",
};

/**
 * Add or Edit a Tag
 * @param {Object} tagData { id, tagName, colorCode, companyId, userId, tagTypeId }
 *   tagTypeId: 1 = General (Lead tags, default), 2 = Selection (Job Selection location tags)
 * @returns {Promise} Response with tag data
 */
export const addEditTag = async (tagData) => {
  try {
    const payload = {
      ...tagData,
      companyId: tagData.companyId || getCompanyIdFromToken(),
      userId: tagData.userId || getUserIdFromToken(),
      tagTypeId: tagData.tagTypeId || 1,
    };

    const response = await axiosInstance.post(
      `${API_ENDPOINTS.LEADS}/addEditTag`,
      payload
    );
    const responseData = response.data;

    if (responseData.statusCode === 200 && responseData.status === "Success") {
      return {
        success: true,
        data: responseData.data,
        message: responseData.message || "Tag saved successfully",
      };
    }
    throw new Error(responseData.message || "Failed to save tag");
  } catch (error) {
    console.error("Error in addEditTag:", error);
    return {
      success: false,
      error: error.message || "Failed to save tag",
      data: null,
    };
  }
};

/**
 * Delete a Tag
 * @param {string} tagId UUID of the tag to delete
 * @param {boolean} [confirmCascade=false] Selection-type tags in use on a JobSelectionItem come
 *   back as inUse:true (not an error) instead of deleting, with a warning message + count, on the
 *   first call. Re-call with confirmCascade=true to proceed anyway -- this clears LocationTagId on
 *   every JobSelectionItem using it, then deletes the tag.
 * @returns {Promise} Response with result; { success:false, inUse:true, message, count } when the
 *   tag is in use and confirmCascade wasn't passed.
 */
export const deleteTag = async (tagId, confirmCascade = false) => {
  try {
    if (!tagId) {
      throw new Error("Tag ID is required for deletion");
    }

    const response = await axiosInstance.post(
      `${API_ENDPOINTS.LEADS}/deleteTag`,
      { tagId, confirmCascade }
    );
    const responseData = response.data;

    if (responseData.statusCode === 200 && responseData.status === "Success") {
      return {
        success: true,
        data: responseData.data,
        message: responseData.message || "Tag deleted successfully",
      };
    }
    if (responseData.statusCode === 409 && responseData.status === "Warning") {
      return {
        success: false,
        inUse: true,
        count: Number(responseData.data) || 0,
        message: responseData.message,
      };
    }
    throw new Error(responseData.message || "Failed to delete tag");
  } catch (error) {
    console.error("Error in deleteTag:", error);
    return {
      success: false,
      error: error.message || "Failed to delete tag",
      data: null,
    };
  }
};

/**
 * Checks what deleting a tag would do, without deleting anything -- lets the caller decide which
 * single confirmation dialog to show (the "in use" warning, or a plain "are you sure?") before the
 * user has confirmed anything, instead of always showing the plain one first and only discovering
 * the tag is in use after that.
 * @param {string} tagId
 * @returns {Promise<{success:boolean, inUse?:boolean, count?:number, blocked?:boolean, message?:string, error?:string}>}
 *   blocked:true means deletion isn't possible at all (e.g. a Lead tag still applied to a Lead).
 */
export const checkTagDeleteImpact = async (tagId) => {
  try {
    if (!tagId) {
      throw new Error("Tag ID is required");
    }

    const response = await axiosInstance.post(
      `${API_ENDPOINTS.LEADS}/deleteTag`,
      { tagId, checkOnly: true }
    );
    const responseData = response.data;

    if (responseData.statusCode === 200 && responseData.status === "Success") {
      return { success: true, inUse: false };
    }
    if (responseData.statusCode === 409 && responseData.status === "Warning") {
      return {
        success: true,
        inUse: true,
        count: Number(responseData.data) || 0,
        message: responseData.message,
      };
    }
    // 403 (e.g. still applied to a Lead) -- deletion is blocked outright.
    return { success: false, blocked: true, error: responseData.message || "This tag cannot be deleted" };
  } catch (error) {
    console.error("Error in checkTagDeleteImpact:", error);
    return { success: false, error: error.message || "Failed to check tag usage" };
  }
};

/**
 * Get all tags for the current company
 * @param {number} pageNumber
 * @param {number} pageSize
 * @param {string} search
 * @param {number} [tagTypeId] 1 = General (Lead tags), 2 = Selection (Job Selection location tags).
 *   Omit to get every type (only used by admin/manage-all-tags contexts, if any).
 * @returns {Promise} Response with array of tags
 */
export const getTagsByCompanyId = async (pageNumber = 1, pageSize = 10000, search = "", tagTypeId = undefined) => {
  try {
    const response = await axiosInstance.get(
      `${API_ENDPOINTS.LEADS}/getTagsByCompanyId`,
      {
        params: {
          pageNumber,
          pageSize,
          search,
          tagTypeId
        }
      }
    );
    const responseData = response.data;

    if (responseData.statusCode === 200 && responseData.status === "Success") {
      let tags = [];
      if (typeof responseData.data === "string") {
        try {
          tags = JSON.parse(responseData.data);
        } catch (e) {
          console.error("Error parsing tags data:", e);
          tags = [];
        }
      } else if (Array.isArray(responseData.data)) {
        tags = responseData.data;
      }

      let totalCount = 0;
      if (responseData.totalRecords !== undefined) {
        totalCount = responseData.totalRecords;
    
      } else if (tags.length > 0) {
        const first = tags[0];
        totalCount =  first.totalRecords;
      }

      return {
        success: true,
        data: tags,
        totalCount: totalCount,
        totalRecords: totalCount,
        message: responseData.message || "Tags fetched successfully",
      };
    }
    throw new Error(responseData.message || "Failed to fetch tags");
  } catch (error) {
    console.error("Error in getTagsByCompanyId:", error);
    return {
      success: false,
      error: error.message || "Failed to fetch tags",
      data: [],
    };
  }
};

/**
 * Upsert tags for a specific lead
 * @param {Object} payload { leadId, tags: [ "tagId1", "tagId2", ... ] }
 * @returns {Promise} Response with result
 */
export const upsertLeadTags = async (payload) => {
  try {
    if (!payload.leadId) {
      throw new Error("Lead ID is required for upserting tags");
    }

    const response = await axiosInstance.post(
      `${API_ENDPOINTS.LEADS}/upsertLeadTags`,
      payload
    );
    const responseData = response.data;

    if (responseData.statusCode === 200 && responseData.status === "Success") {
      return {
        success: true,
        data: responseData.data,
        message: responseData.message || "Lead tags updated successfully",
      };
    }
    throw new Error(responseData.message || "Failed to update lead tags");
  } catch (error) {
    console.error("Error in upsertLeadTags:", error);
    return {
      success: false,
      error: error.message || "Failed to update lead tags",
      data: null,
    };
  }
};
